/**
 * printLayout.js — Print stylesheet unificato Gruppo Impresa Suite
 * v1.6.0 · 22/07/2026
 *
 * Fornisce il layout HTML/CSS standard per le stampe A4 (portrait o
 * landscape) del gestionale. Header studio con logo/nome + wave brand,
 * corpo scrollabile per la stampa, footer istituzionale a ogni pagina
 * con: data stampa · Suite · Banca dati · versione + data · pagina X/Y.
 *
 * Uso:
 *   import { openPrintWindow, buildPrintDocument } from '@/utils/printLayout';
 *   const html = buildPrintDocument({ title, subtitle, bodyHtml, orientation: 'landscape' });
 *   openPrintWindow(html);
 */
import { APP_RELEASE } from "@/config/appRelease";

const BRAND_GREEN = "#08684F";
const BRAND_GREEN_DARK = "#044A38";
const BRAND_GREEN_50 = "#E6F6EE";
const BRAND_GOLD = "#F2C94C";

function formatPrintDate() {
  const d = new Date();
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Costruisce un documento HTML completo pronto per il print. Il caller
 * fornisce solo `bodyHtml` (già escapato) per il contenuto centrale.
 */
export function buildPrintDocument({
  title = "Documento",
  subtitle = "",
  bodyHtml = "",
  orientation = "landscape", // "landscape" | "portrait"
  studioName = "Studio Bonaiuti STP",
  studioLine = "",
} = {}) {
  const pageSize = orientation === "portrait" ? "A4" : "A4 landscape";
  const printedAt = formatPrintDate();

  return `<!doctype html>
<html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
<style>
  @page { size: ${pageSize}; margin: 12mm 10mm 20mm 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, "Helvetica Neue", Arial, sans-serif;
    color: #0f172a;
    font-size: 10.5pt;
    line-height: 1.35;
    background: white;
  }

  /* ── HEADER ── */
  .gis-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid ${BRAND_GREEN};
    margin-bottom: 6px;
  }
  .gis-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .gis-dots { display: inline-flex; gap: 4px; }
  .gis-dots span {
    width: 8px; height: 8px; border-radius: 50%; display: inline-block;
  }
  .gis-dot-red   { background: #E74C3C; }
  .gis-dot-gray  { background: #95A5A6; }
  .gis-dot-gold  { background: ${BRAND_GOLD}; }
  .gis-brand-name {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-weight: 700;
    font-size: 10pt;
    letter-spacing: 0.06em;
    color: #0f172a;
  }
  .gis-studio {
    text-align: right;
    font-size: 9pt;
    color: #475569;
    line-height: 1.3;
  }
  .gis-studio-name {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-weight: 600;
    color: ${BRAND_GREEN};
    font-size: 10pt;
  }

  /* ── TITLE ── */
  .gis-title-block { margin: 8px 0 4px; }
  h1.gis-title {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-size: 16pt;
    font-weight: 700;
    color: ${BRAND_GREEN_DARK};
    margin: 0 0 2px;
  }
  .gis-subtitle {
    font-size: 9.5pt;
    color: #64748b;
    margin: 0;
  }

  /* wave */
  .gis-wave { height: 6px; margin-top: 4px; background:
    linear-gradient(90deg,
      ${BRAND_GREEN} 0%, ${BRAND_GREEN} 40%,
      ${BRAND_GOLD} 40%, ${BRAND_GOLD} 55%,
      ${BRAND_GREEN} 55%, ${BRAND_GREEN} 100%);
    border-radius: 3px;
    opacity: 0.85;
  }

  /* ── BODY ── */
  .gis-body { margin-top: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  th, td {
    border: 1px solid #cbd5e1;
    padding: 5px 7px;
    text-align: left;
    vertical-align: top;
    word-wrap: break-word;
  }
  th {
    background: ${BRAND_GREEN_50};
    color: ${BRAND_GREEN_DARK};
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 8pt;
    letter-spacing: 0.04em;
    border-bottom: 2px solid ${BRAND_GREEN};
  }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  h2 {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-size: 11pt;
    color: ${BRAND_GREEN};
    margin: 16px 0 6px;
    padding-bottom: 3px;
    border-bottom: 1px solid ${BRAND_GREEN_50};
  }

  /* ── FOOTER istituzionale (fisso su ogni pagina via @page + running header) ── */
  .gis-footer {
    position: fixed;
    bottom: 6mm;
    left: 10mm;
    right: 10mm;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-top: 4px;
    border-top: 1px solid ${BRAND_GREEN};
    font-size: 8pt;
    color: #64748b;
  }
  .gis-footer strong { color: ${BRAND_GREEN}; font-weight: 600; }
  .gis-page-num::after {
    content: counter(page) " / " counter(pages);
  }
  @media print {
    .gis-footer { display: flex !important; }
  }
</style></head><body>
  <div class="gis-header">
    <div class="gis-brand">
      <span class="gis-dots" aria-hidden="true">
        <span class="gis-dot-red"></span>
        <span class="gis-dot-gray"></span>
        <span class="gis-dot-gold"></span>
      </span>
      <span class="gis-brand-name">${escapeHtml(APP_RELEASE.suiteName || "GRUPPO IMPRESA SUITE")}</span>
    </div>
    <div class="gis-studio">
      <div class="gis-studio-name">${escapeHtml(studioName)}</div>
      ${studioLine ? `<div>${escapeHtml(studioLine)}</div>` : ""}
    </div>
  </div>

  <div class="gis-title-block">
    <h1 class="gis-title">${escapeHtml(title)}</h1>
    ${subtitle ? `<p class="gis-subtitle">${escapeHtml(subtitle)}</p>` : ""}
    <div class="gis-wave" aria-hidden="true"></div>
  </div>

  <div class="gis-body">${bodyHtml}</div>

  <div class="gis-footer">
    <div>Stampato il ${escapeHtml(printedAt)}</div>
    <div>
      <strong>${escapeHtml(APP_RELEASE.suiteName || "Gruppo Impresa Suite")}</strong>
      &nbsp;·&nbsp; Banca dati: <strong>${escapeHtml(APP_RELEASE.dataSource || "-")}</strong>
      &nbsp;·&nbsp; <strong>v${escapeHtml(APP_RELEASE.version)}</strong>
      (${escapeHtml(APP_RELEASE.updatedAt)})
    </div>
    <div>Pagina <span class="gis-page-num"></span></div>
  </div>

  <script>window.onload = () => setTimeout(() => window.print(), 150);</script>
</body></html>`;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Apre una nuova finestra popup e ci scrive dentro l'HTML pronto per il print. */
export function openPrintWindow(html) {
  const win = window.open("", "_blank", "width=1200,height=900");
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}

